import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import _ from '@/utils/lodash'

import NotFound from '@app/(website)/not-found'
import { useAppContext } from '@/context/AppContext'
import { useScreenShape } from '@/hooks'
import { findPageByPath, followUpExists, getParentId } from '@/utils/pagePaths'
import { CloseButton, NextLink, PrevLink, VideoControls } from '@components/shared/ImageViewer/components'
import MediaFrame from '@components/shared/ImageViewer/components/MediaFrame'
import { useTouchDevice } from '@components/shared/ImageViewer/hooks'
import {
    flattenImages,
    parseVideoParams,
    prepareDesktopSlidesData,
    prepareSlidesData,
} from '@components/shared/ImageViewer/imageViewerUtils'
import { getMediaType, getVideoUrl } from '@/utils/media'
import { trackMediaOpened } from '@/services/analytics'
import { compactMeta, shareImageMeta, shareSiteTitle, resolveMediaShareCard } from '@/share/meta'

/**
 * Dedicated public video route (`.vid`).
 *
 * Supports:
 *  - /:video.vid             (home gallery video)
 *  - /:slug/:video.vid       (page gallery video, incl. nested slugs)
 *
 * Autoplay + visible controls only happen here, on the dedicated route. In the
 * gallery the video is shown as a static poster thumbnail that links here.
 */
export default function PublicVideoRoute({ match: matchProp, location: locationProp }) {
    const hookParams = useParams()
    const hookLocation = useLocation()
    const navigate = useNavigate()
    const screenShape = useScreenShape()
    const isTouchDevice = useTouchDevice()
    const match = matchProp ?? { params: hookParams }
    const location = locationProp ?? hookLocation
    const { content, urlBase, theme } = useAppContext()
    const videoRef = useRef(null)
    const lastSavedSecondRef = useRef(-1)
    // Blocks a second navigation until the next video is actually showing.
    // State would stay true: this route stays mounted when only the .vid id changes.
    const navLockRef = useRef(false)
    const [isClosing, setIsClosing] = useState(false)
    const [isVideoReady, setIsVideoReady] = useState(false)
    const [duration, setDuration] = useState(0)
    const [currentTime, setCurrentTime] = useState(0)
    const [isPlaying, setIsPlaying] = useState(false)

    const parsed = parseVideoParams(match, location)
    const videoId = parsed.videoId
    const slug = parsed.slug
    const playbackStorageKey = videoId ? `videoPlaybackPosition:${videoId}` : null

    const isContentReady = Boolean(content && content.files && content.home && content.pages)
    const pages = content?.pages || {}
    let pageData = null
    if (slug) {
        pageData = findPageByPath(slug, pages)
        if (!pageData) {
            pageData = _.result(content, ['pages', slug], null)
        }
    }

    const galleryType = slug && pageData
        ? (pageData.gallery?.type || pageData.gallery)
        : (content?.home?.gallery?.type || content?.home?.gallery)

    const rawImages = slug && pageData
        ? (pageData.gallery?.images || [])
        : _.result(content, 'home.gallery.images', [])

    const flatImages = flattenImages(rawImages)
    const file = videoId ? _.result(content, ['files', videoId], null) : null
    const mediaIndex = flatImages.indexOf(videoId)
    const pageName = pageData?.name || 'Home'
    const grandparentName = (() => {
        if (!pageData || !slug) return null
        const parentId = getParentId(pageData)
        if (!parentId) return null
        const parentPage = pages[parentId]
        if (!parentPage) return null
        const grandparentId = getParentId(parentPage)
        if (grandparentId && pages[grandparentId]) return pages[grandparentId].name
        return parentPage.name
    })()
    const canTrack = Boolean(videoId && file && getMediaType(file) === 'video')

    useEffect(() => {
        navLockRef.current = false
        setIsClosing(false)
        setIsVideoReady(false)
        setIsPlaying(false)
        setCurrentTime(0)
        setDuration(0)
        lastSavedSecondRef.current = -1
        const el = videoRef.current
        if (!el) return
        if (Number.isFinite(el.duration) && el.duration > 0) {
            setDuration(el.duration)
            setCurrentTime(el.currentTime || 0)
            setIsVideoReady(el.readyState >= 2)
            setIsPlaying(!el.paused)
        }
    }, [videoId])

    useEffect(() => {
        return () => {
            const el = videoRef.current
            if (!el || !playbackStorageKey || typeof window === 'undefined') return
            try {
                localStorage.setItem(playbackStorageKey, String(el.currentTime || 0))
            } catch (_) {}
        }
    }, [playbackStorageKey])

    useEffect(() => {
        if (!canTrack) return
        trackMediaOpened({
            imageName: videoId,
            imageDescription: file?.description,
            originalName: file?.originalname,
            pageName,
            pageDescription: pageData?.description,
            grandparentName,
            imageIndex: mediaIndex >= 0 ? mediaIndex + 1 : null,
            totalImages: flatImages.length,
            mediaType: 'video',
        })
    }, [
        canTrack,
        videoId,
        file?.description,
        file?.originalname,
        pageName,
        pageData?.description,
        grandparentName,
        mediaIndex,
        flatImages.length,
    ])

    const page = slug
    const pagesObj = pages
    const files = content?.files || {}
    // A version can remove the follow-up page; then the last video just closes.
    const advanceUrl = followUpExists(pageData?.advanceUrl, pages) ? pageData.advanceUrl : null
    const closeUrl = slug ? `/${slug}` : '/'
    const isDesktop = screenShape !== 'small'
    const desktopSlides = prepareDesktopSlidesData(rawImages, files, page, pagesObj)
    const mobileSlides = prepareSlidesData(rawImages, files, page, pagesObj)
    const desktopIndex = desktopSlides.findIndex((slide) => (
        slide.id === videoId || slide.images?.some((image) => image.id === videoId)
    ))
    const isFirstDesktop = desktopIndex <= 0
    const isLastDesktop = desktopIndex === desktopSlides.length - 1
    const prevDesktop = desktopIndex > 0 ? desktopSlides[desktopIndex - 1] : null
    const nextDesktop = desktopIndex >= 0 && desktopIndex < desktopSlides.length - 1
        ? desktopSlides[desktopIndex + 1]
        : null
    const mobileIndex = mobileSlides.findIndex((slide) => slide.id === videoId)
    const isFirstMobile = mediaIndex === 0
    const isLastMobile = mediaIndex >= 0 && mediaIndex === flatImages.length - 1
    const prevMobile = mobileIndex > 0 ? mobileSlides[mobileIndex - 1] : null
    const nextMobile = mobileIndex >= 0 && mobileIndex < mobileSlides.length - 1
        ? mobileSlides[mobileIndex + 1]
        : null
    const mobileUrlPrev = isFirstMobile && page ? `/${page}` : (prevMobile?.url || null)
    const mobileUrlNext = isLastMobile && advanceUrl ? advanceUrl : (nextMobile?.url || null)
    const desktopUrlPrev = isFirstDesktop && page ? `/${page}` : (prevDesktop?.url || null)
    const desktopUrlNext = isLastDesktop && advanceUrl ? advanceUrl : (nextDesktop?.url || null)
    const urlPrev = isDesktop ? desktopUrlPrev : mobileUrlPrev
    const urlNext = isDesktop ? desktopUrlNext : mobileUrlNext
    const navIsFirst = isDesktop ? isFirstDesktop : isFirstMobile
    const navIsLast = isDesktop ? isLastDesktop : isLastMobile

    useEffect(() => {
        const onKeyDown = (event) => {
            const tag = event.target?.tagName?.toLowerCase()
            const type = String(event.target?.type || '').toLowerCase()
            const isText = tag === 'textarea' || tag === 'select' || event.target?.isContentEditable
                || (tag === 'input' && type !== 'range')
            if (isText) return

            const go = (url, { focus = false, leaving = false } = {}) => {
                if (!url || navLockRef.current || isClosing) return
                navLockRef.current = true
                event.preventDefault()
                if (focus && videoId) sessionStorage.setItem('focusImageId', videoId)
                // Only leaving the viewer should ignore further keys. Moving to the
                // next video reuses this component, so a sticky flag would freeze it.
                if (leaving) setIsClosing(true)
                const el = videoRef.current
                if (el && !el.paused) el.pause()
                navigate(url)
            }

            if (event.key === 'Escape' || event.key === 'Enter' || event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                go(closeUrl, { focus: true, leaving: true })
                return
            }
            if (event.repeat) return
            if (event.key === 'ArrowLeft') {
                event.preventDefault()
                if (navIsFirst && urlPrev) go(urlPrev)
                else if (!navIsFirst) go(urlPrev)
                return
            }
            if (event.key === 'ArrowRight') {
                event.preventDefault()
                const leaving = Boolean(navIsLast && advanceUrl && urlNext === advanceUrl)
                go(urlNext, { leaving })
                return
            }
            if (event.key === ' ' || event.code === 'Space') {
                if (tag === 'button') return
                event.preventDefault()
                const el = videoRef.current
                if (!el) return
                if (el.paused) el.play().catch(() => {})
                else el.pause()
            }
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [urlPrev, urlNext, navIsFirst, navIsLast, advanceUrl, closeUrl, navigate, videoId, isClosing])

    // Videos live in image galleries (masonry / slideshow).
    const isImageGallery = galleryType === 'masonry' || galleryType === 'slideshow'
    if (!isContentReady) {
        return (
            <div
                className="fixed inset-0 z-40"
                style={{ backgroundColor: 'var(--color-background)' }}
            />
        )
    }
    if (!isImageGallery) {
        return <NotFound />
    }

    // Must exist, be part of this gallery, and actually be a video.
    if (!videoId || !flatImages.includes(videoId) || !file || getMediaType(file) !== 'video') {
        return <NotFound />
    }

    const videoSrc = getVideoUrl(file)
    if (!videoSrc) {
        return <NotFound />
    }

    const description = file.description || ''
    const siteTitle = shareSiteTitle(content, theme)
    const title = description || siteTitle
    const baseUrl = String(urlBase || '').replace(/\/$/, '')
    const { title: shareTitle, description: shareDescription } = resolveMediaShareCard(file, {
        content,
        theme,
        page: pageData,
        pages,
        isHome: !slug,
    })
    const videoAbsoluteUrl = `${baseUrl}${videoSrc}`
    const helmetMeta = compactMeta([
        { property: 'og:type', content: 'video.other' },
        { property: 'og:title', content: shareTitle },
        { property: 'og:description', content: shareDescription },
        { name: 'description', content: shareDescription },
        ...shareImageMeta(file, baseUrl),
        { property: 'og:video', content: videoAbsoluteUrl },
        { property: 'og:video:secure_url', content: videoAbsoluteUrl.startsWith('https:') ? videoAbsoluteUrl : '' },
        { property: 'og:video:type', content: 'video/mp4' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: shareTitle },
        { name: 'twitter:description', content: shareDescription },
    ])
    const togglePlay = () => {
        const el = videoRef.current
        if (!el) return
        if (el.paused) {
            el.play().catch(() => {})
        } else {
            el.pause()
        }
    }

    const handleSeek = (value) => {
        const el = videoRef.current
        if (!el) return
        const t = Number(value)
        if (!Number.isFinite(t)) return
        const clampedTime = duration > 0 ? Math.max(0, Math.min(t, duration)) : Math.max(0, t)
        el.currentTime = clampedTime
        setCurrentTime(clampedTime)
        if (playbackStorageKey && typeof window !== 'undefined') {
            try {
                localStorage.setItem(playbackStorageKey, String(clampedTime))
            } catch (_) {}
        }
    }

    const startAutoplay = () => {
        const el = videoRef.current
        if (!el) return
        const playPromise = el.play()
        if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {
                setIsPlaying(false)
            })
        }
    }

    const handleClose = (e) => {
        if (isClosing) {
            if (e) e.preventDefault()
            return
        }
        setIsClosing(true)
        if (videoId && typeof window !== 'undefined') {
            sessionStorage.setItem('focusImageId', videoId)
        }
    }

    return (
        <div
            className="fixed inset-0 z-40 flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-background)' }}
        >
            <Helmet meta={helmetMeta}>
                {title && <title>{title}</title>}
            </Helmet>

            <CloseButton url={closeUrl} onClick={handleClose} />
            {urlPrev && !isTouchDevice && !navIsFirst && (
                <PrevLink
                    url={urlPrev}
                    onClick={(event) => {
                        event.preventDefault()
                        if (isClosing) return
                        navigate(urlPrev)
                    }}
                />
            )}
            {urlNext && !isTouchDevice && (
                <NextLink
                    url={urlNext}
                    onClick={(event) => {
                        event.preventDefault()
                        if (isClosing) return
                        if (navIsLast && advanceUrl && videoId) {
                            sessionStorage.setItem('focusImageId', videoId)
                        }
                        navigate(urlNext)
                    }}
                />
            )}

            <div className="relative h-full w-full">
                    <MediaFrame>
                        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                        <video
                            ref={videoRef}
                            src={videoSrc}
                            className={`max-w-full max-h-full h-full w-full object-contain transition-opacity duration-150 cursor-pointer ${isVideoReady ? 'opacity-100' : 'opacity-0'}`}
                            preload="auto"
                            autoPlay
                            playsInline
                            loop
                            tabIndex={-1}
                            onKeyDown={(event) => {
                                if (event.key === 'ArrowLeft' || event.key === 'ArrowRight' || event.key === 'Escape') {
                                    event.preventDefault()
                                }
                            }}
                            onClick={togglePlay}
                            onLoadedData={() => {
                                setIsVideoReady(true)
                                startAutoplay()
                            }}
                            onLoadedMetadata={(e) => {
                                const d = e.currentTarget.duration || 0
                                setDuration(d)
                                if (!playbackStorageKey || typeof window === 'undefined') return
                                try {
                                    const saved = Number(localStorage.getItem(playbackStorageKey))
                                    if (!Number.isFinite(saved) || saved <= 0) return
                                    const resumeTime = Math.max(0, Math.min(saved, Math.max(0, d - 0.25)))
                                    e.currentTarget.currentTime = resumeTime
                                    setCurrentTime(resumeTime)
                                } catch (_) {}
                            }}
                            onTimeUpdate={(e) => {
                                const t = e.currentTarget.currentTime || 0
                                setCurrentTime(t)
                                if (!playbackStorageKey || typeof window === 'undefined') return
                                const wholeSecond = Math.floor(t)
                                if (wholeSecond === lastSavedSecondRef.current) return
                                lastSavedSecondRef.current = wholeSecond
                                try {
                                    localStorage.setItem(playbackStorageKey, String(t))
                                } catch (_) {}
                            }}
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                        />
                    </MediaFrame>
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 px-4 pb-4">
                        <div className="pointer-events-auto">
                            <VideoControls
                                isPlaying={isPlaying}
                                currentTime={currentTime}
                                duration={duration}
                                description={description}
                                onToggle={togglePlay}
                                onSeek={handleSeek}
                            />
                        </div>
                    </div>
            </div>
        </div>
    )
}

PublicVideoRoute.propTypes = {
    match: PropTypes.object,
    location: PropTypes.object,
}
